import BigNumber from 'bignumber.js'
import { DEFAULT_TOKEN_DECIMAL, OPERAHOUSE_GRAPHQL_URL, PAINTSWAP_API_URL } from 'config'
import NftABI from 'config/abi/nft.json'
import NftV2ABI from 'config/abi/nftV2.json'
import NftPoolWithMcABI from 'config/abi/nftPoolWithMC.json'
import NftPoolABI from 'config/abi/nftPool.json'
import MasterNftABI from 'config/abi/masterNft.json'
import { getAddress } from 'utils/addressHelpers'
import multicall, { multicallWithAbi } from 'utils/multicall'
import { BIG_TEN, BIG_ZERO } from 'utils/bigNumber'
import { getBalanceAmount } from 'utils/formatBalance'
import { Series } from '../types'

type PublicSeriesData = {
  price: number
  maxSupply: number
  marketPrice: number
  minted: number
  totalStaked: number
  maxMintPerTx: number
  paused: boolean
  onlyWhitelisted: boolean
  stakingPaused: boolean
  startStakingTime: number
  totalStakedAbility: number
  tokenPerSecond: number
  totalMintAmount: number
  mintedAmount: number
}

const fetchPrices = async (serieses: Series[]) => {
  const seriesesToFetch = serieses.filter((series) => series.getPriceFunc)
  const calls = seriesesToFetch.map((series) => ({
    address: getAddress(series.address),
    name: series.getPriceFunc,
  }))
  const pricesFC = await multicall(NftABI, calls)

  if (pricesFC) {
    return seriesesToFetch.reduce(
      (acc, series, index) => ({
        ...acc,
        [series.sid]: series.notEtherPrice
          ? new BigNumber(pricesFC[index]).toNumber()
          : getBalanceAmount(pricesFC[index], series.token?.decimals || 18).toNumber(),
      }),
      {},
    )
  } else {
    return {}
  }
}

const fetchMaxSupplies = async (serieses: Series[]) => {
  const seriesesToFetch = serieses.filter((series) => series.maxSupplyFunc)
  const calls = seriesesToFetch.map((series) => ({
    address: getAddress(series.address),
    name: series.maxSupplyFunc,
  }))
  const maxSuppliesFC = await multicall(NftABI, calls)

  return seriesesToFetch.reduce(
    (acc, series, index) => ({
      ...acc,
      [series.sid]: new BigNumber(maxSuppliesFC[index]).toNumber(),
    }),
    {},
  )
}

const fetchOnlyWhitelisteds = async (serieses: Series[]) => {
  const seriesesToFetch = serieses.filter((series) => series.isLaunchpad)
  const calls = seriesesToFetch.map((series) => ({
    address: getAddress(series.address),
    name: 'onlyWhitelisted',
  }))
  const onlyWhitelistedsFC = await multicall(NftABI, calls)

  if (onlyWhitelistedsFC) {
    return seriesesToFetch.reduce(
      (acc, series, index) => ({
        ...acc,
        [series.sid]: onlyWhitelistedsFC[index][0],
      }),
      {},
    )
  } else {
    return {}
  }
}

const fetchMaxMintsPerTx = async (serieses: Series[]) => {
  const seriesesToFetch = serieses.filter((series) => series.isLaunchpad)
  const calls = seriesesToFetch.map((series) => ({
    address: getAddress(series.address),
    name: 'maxMintPerTx',
  }))
  const maxMintsPerTxFC = await multicall(NftABI, calls)

  return seriesesToFetch.reduce(
    (acc, series, index) => ({
      ...acc,
      [series.sid]: new BigNumber(maxMintsPerTxFC[index]).toNumber(),
    }),
    {},
  )
}

export const fetchMinteds = async (serieses: Series[]) => {
  const seriesesToFetch = serieses.filter((series) => !series.preTotalSupply)
  const calls = seriesesToFetch.map((series) => ({
    address: getAddress(series.address),
    name: 'totalSupply',
  }))
  const mintedsFC = await multicall(NftABI, calls)

  return seriesesToFetch.reduce(
    (acc, series, index) => ({
      ...acc,
      [series.sid]: new BigNumber(mintedsFC[index]).toNumber(),
    }),
    {},
  )
}

const fetchTotalStakeds = async (serieses: Series[]) => {
  const seriesesToFetch = serieses.filter((series) => series.pool)
  const calls = seriesesToFetch.map((series) => ({
    address: getAddress(series.address),
    name: 'balanceOf',
    params: [getAddress(series.pool)],
  }))
  const totalStakedsFC = await multicall(NftABI, calls)

  return seriesesToFetch.reduce(
    (acc, series, index) => ({
      ...acc,
      [series.sid]: new BigNumber(totalStakedsFC[index]).toNumber(),
    }),
    {},
  )
}

const fetchStakingPauseds = async (serieses: Series[]) => {
  const seriesesToFetch = serieses.filter((series) => series.pool)
  const calls = seriesesToFetch.map((series) => ({
    address: getAddress(series.pool),
    name: 'paused',
  }))
  const stakingPausedsFC = await multicall(NftPoolABI, calls)

  return seriesesToFetch.reduce(
    (acc, series, index) => ({
      ...acc,
      [series.sid]: stakingPausedsFC[index][0],
    }),
    {},
  )
}

const fetchPauseds = async (serieses: Series[]) => {
  const seriesesToFetch = serieses.filter((series) => series.key.startsWith('series') || series.isLaunchpad)
  const calls = seriesesToFetch.map((series) => ({
    address: getAddress(series.address),
    name: series.token ? 'pausedERC20' : 'paused',
  }))
  const pausedsFC = await multicall(NftV2ABI, calls)

  return seriesesToFetch.reduce(
    (acc, series, index) => ({
      ...acc,
      [series.sid]: pausedsFC[index][0],
    }),
    {},
  )
}

const fetchStartStakingTimes = async (serieses: Series[]) => {
  const seriesesToFetch = serieses.filter((series) => series.pool && !series.withoutMC)
  const calls = seriesesToFetch.map((series) => ({
    address: getAddress(series.pool),
    name: 'startTime',
  }))
  const startStakingTimesFC = await multicall(NftPoolWithMcABI, calls)

  return seriesesToFetch.reduce(
    (acc, series, index) => ({
      ...acc,
      [series.sid]: new BigNumber(startStakingTimesFC[index]).toNumber(),
    }),
    {},
  )
}

const fetchTotalStakedAbilities = async (serieses: Series[]) => {
  const seriesesToFetch = serieses.filter((series) => series.pool && !series.withoutMC && series.abilityKey)
  const calls = seriesesToFetch.map((series) => ({
    address: getAddress(series.pool),
    name: 'poolInfoMap',
    params: [getAddress(series.address)],
  }))
  const poolInfosFC = await multicall(NftPoolWithMcABI, calls)

  return seriesesToFetch.reduce(
    (acc, series, index) => ({
      ...acc,
      [series.sid]: new BigNumber(poolInfosFC[index][3]).div(DEFAULT_TOKEN_DECIMAL).toNumber(),
    }),
    {},
  )
}

const fetchTokenPerSeconds = async (serieses: Series[]) => {
  const seriesesToFetch = serieses.filter((series) => series.pool)
  const calls = seriesesToFetch.map((series) =>
    series.withoutMC
      ? {
          address: getAddress(series.dmdMaster),
          name: 'tokenPerSecond',
          abi: MasterNftABI,
        }
      : {
          address: getAddress(series.pool),
          name: 'DMDPerSecond',
          abi: NftPoolWithMcABI,
        },
  )
  const tokenPerSecondsFC = await multicallWithAbi(calls)

  return seriesesToFetch.reduce(
    (acc, series, index) => ({
      ...acc,
      [series.sid]: new BigNumber(tokenPerSecondsFC[index]).div(DEFAULT_TOKEN_DECIMAL).toNumber(),
    }),
    {},
  )
}

const fetchTotalMintAmounts = async (serieses: Series[]) => {
  const seriesesToFetch = serieses.filter((series) => series.pool && (series.withoutMC || !series.isMasterChefPool))
  const calls = seriesesToFetch.map((series) =>
    series.withoutMC
      ? {
          address: getAddress(series.dmdMaster),
          name: 'TotalMintAmount',
          abi: MasterNftABI,
        }
      : {
          address: getAddress(series.pool),
          name: 'totalMintAmount',
          abi: NftPoolWithMcABI,
        },
  )
  const totalMintAmountsFC = await multicallWithAbi(calls)

  if (totalMintAmountsFC) {
    return seriesesToFetch.reduce(
      (acc, series, index) => ({
        ...acc,
        [series.sid]: new BigNumber(totalMintAmountsFC[index])
          .div(BIG_TEN.pow(series.rewardToken?.decimals || 18))
          .toNumber(),
      }),
      {},
    )
  } else {
    return {}
  }
}

const fetchMintedAmounts = async (serieses: Series[]) => {
  const seriesesToFetch = serieses.filter((series) => series.pool && (series.withoutMC || !series.isMasterChefPool))
  const calls = seriesesToFetch.map((series) => ({
    address: series.withoutMC ? getAddress(series.dmdMaster) : getAddress(series.pool),
    name: 'mintedAmount',
    abi: MasterNftABI,
  }))
  const mintedAmountsFC = await multicallWithAbi(calls)

  return seriesesToFetch.reduce(
    (acc, series, index) => ({
      ...acc,
      [series.sid]: new BigNumber(mintedAmountsFC[index])
        .div(BIG_TEN.pow(series.rewardToken?.decimals || 18))
        .toNumber(),
    }),
    {},
  )
}

const fetchMarketPrices = async (serieses: Series[]) => {
  const data = await Promise.all(
    serieses.map(async (series) => {
      if (series.isFinished) {
        return series
      }

      const totalStakedAbility = series.totalStakedAbility ? series.totalStakedAbility : 0
      const totalStaked = series.totalStaked ? series.totalStaked : 0
      let marketPrice = series.marketPrice ? series.marketPrice : 0
      const { address, pool } = series

      try {
        if (series.paintswap) {
          const response = await fetch(`${PAINTSWAP_API_URL}/v2/collections/${getAddress(address).toLowerCase()}`)
          const jsonData = await response.json()
          if (jsonData) {
            const stats = jsonData.collection?.stats
            if (parseInt(stats.sale.numTradesLast7Days) > 0) {
              marketPrice = new BigNumber(stats.sale.volumeLast7Days)
                .div(stats.sale.numTradesLast7Days)
                .div(DEFAULT_TOKEN_DECIMAL)
                .toNumber()
            } else if (parseInt(stats.sale.totalVolumeTradedFTM) > 0) {
              marketPrice = new BigNumber(stats.sale.totalVolumeTradedFTM)
                .div(stats.sale.totalTrades)
                .div(DEFAULT_TOKEN_DECIMAL)
                .toNumber()
            } else {
              marketPrice = new BigNumber(stats.averagePrice).div(DEFAULT_TOKEN_DECIMAL).toNumber()
            }
          }
        } else if (series.nftkeyColName) {
          const response = await fetch(`${process.env.REACT_APP_API}/nft/nftkey/sales/${getAddress(address)}`)
          const jsonData = await response.json()
          const marketTokenLists = jsonData.data
          if (marketTokenLists.length) {
            let total = 0
            let ability = 0
            const calls1 = []
            marketTokenLists.forEach((item) => {
              total += item.valueNum
              if (series.abilityKey) {
                calls1.push({
                  address: getAddress(pool),
                  name: 'nftTokenstakingAbilityMap',
                  params: [getAddress(address), item.tokenId],
                })
              }
            })
            if (series.abilityKey) {
              const abilitiesFC = await multicall(NftPoolWithMcABI, calls1)
              abilitiesFC?.forEach((abilityFC) => {
                ability += new BigNumber(abilityFC).div(DEFAULT_TOKEN_DECIMAL).toNumber()
              })
            }
            if (ability) {
              marketPrice = ((totalStakedAbility / totalStaked) * total) / ability
            } else {
              marketPrice = total / marketTokenLists.length
            }
          }
        } else if (series.operaHouse) {
          const query = `{
            histories(
              first: 100,
              where: {
                nftContract: "${getAddress(address)}"
              },
              orderBy: time,
              orderDirection: desc
            ) {
              amount,
              price,
              paymentToken,
              time
            }
          }`
          const response = await fetch(OPERAHOUSE_GRAPHQL_URL, {
            method: 'POST',
            body: JSON.stringify({ query }),
          })
          const jsonData = await response.json()
          const histories = jsonData.data.histories

          const amount = histories.reduce((res, item) => res + parseInt(item.amount), 0)
          const total = histories.reduce(
            (res, item) =>
              res.plus(new BigNumber(item.price).div(DEFAULT_TOKEN_DECIMAL).multipliedBy(new BigNumber(item.amount))),
            BIG_ZERO,
          )
          marketPrice = total.toNumber() / amount
        }
      } catch (error) {
        console.error(error)
      }
      return { ...series, marketPrice }
    }),
  )

  return data.reduce(
    (acc, series, index) => ({
      ...acc,
      [series.sid]: data[index].marketPrice,
    }),
    {},
  )
}

const fetchPublicSeriesesData = async (serieses: Series[]): Promise<PublicSeriesData[]> => {
  let prices = {}
  let marketPrices = {}
  let maxSupplies = {}
  let onlyWhitelisteds = {}
  let maxMintsPerTx = {}
  let minteds = {}
  let totalStakeds = {}
  let stakingPauseds = {}
  let pauseds = {}
  let startStakingTimes = {}
  let totalStakedAbilities = {}
  let tokenPerSeconds = {}
  let totalMintAmounts = {}
  let mintedAmounts = {}

  const setPrices = async () => {
    prices = await fetchPrices(serieses)
  }
  const setMaxSupplies = async () => {
    maxSupplies = await fetchMaxSupplies(serieses)
  }
  const setOnlyWhitelisteds = async () => {
    onlyWhitelisteds = await fetchOnlyWhitelisteds(serieses)
  }
  const setMaxMintsPerTx = async () => {
    maxMintsPerTx = await fetchMaxMintsPerTx(serieses)
  }
  const setMinteds = async () => {
    minteds = await fetchMinteds(serieses)
  }
  const setTotalStakeds = async () => {
    totalStakeds = await fetchTotalStakeds(serieses)
  }
  const setStakingPauseds = async () => {
    stakingPauseds = await fetchStakingPauseds(serieses)
  }
  const setPauseds = async () => {
    pauseds = await fetchPauseds(serieses)
  }
  const setStartStakingTimes = async () => {
    startStakingTimes = await fetchStartStakingTimes(serieses)
  }
  const setTotalStakedAbilities = async () => {
    totalStakedAbilities = await fetchTotalStakedAbilities(serieses)
  }
  const setTokenPerSeconds = async () => {
    tokenPerSeconds = await fetchTokenPerSeconds(serieses)
  }
  const setTotalMintAmounts = async () => {
    totalMintAmounts = await fetchTotalMintAmounts(serieses)
  }
  const setMintedAmounts = async () => {
    mintedAmounts = await fetchMintedAmounts(serieses)
  }
  const setMarketPrices = async () => {
    marketPrices = await fetchMarketPrices(serieses)
  }
  await Promise.all([
    setPrices(),
    setMaxSupplies(),
    setOnlyWhitelisteds(),
    setMaxMintsPerTx(),
    setMinteds(),
    setTotalStakeds(),
    setStakingPauseds(),
    setPauseds(),
    setStartStakingTimes(),
    setTotalStakedAbilities(),
    setTokenPerSeconds(),
    setTotalMintAmounts(),
    setMintedAmounts(),
    setMarketPrices(),
  ])

  return serieses.map((series) => ({
    price: prices[series.sid] ? prices[series.sid] : 0,
    maxSupply: maxSupplies[series.sid] ? maxSupplies[series.sid] : 0,
    onlyWhitelisted: onlyWhitelisteds[series.sid] ? onlyWhitelisteds[series.sid] : false,
    maxMintPerTx: maxMintsPerTx[series.sid] ? maxMintsPerTx[series.sid] : 0,
    minted: series.preTotalSupply ? series.preTotalSupply : minteds[series.sid] ? minteds[series.sid] : 0,
    totalStaked: totalStakeds[series.sid] ? totalStakeds[series.sid] : 0,
    stakingPaused: stakingPauseds[series.sid] ? stakingPauseds[series.sid] : false,
    paused: pauseds[series.sid] ? pauseds[series.sid] : false,
    startStakingTime: startStakingTimes[series.sid] ? startStakingTimes[series.sid] : 0,
    totalStakedAbility: totalStakedAbilities[series.sid] ? totalStakedAbilities[series.sid] : 0,
    tokenPerSecond: tokenPerSeconds[series.sid] ? tokenPerSeconds[series.sid] : 0,
    totalMintAmount: totalMintAmounts[series.sid] ? totalMintAmounts[series.sid] : 0,
    mintedAmount: mintedAmounts[series.sid] ? mintedAmounts[series.sid] : 0,
    marketPrice: marketPrices[series.sid],
  }))
}

export default fetchPublicSeriesesData
